/**
 * useOAuthHandler - Extracted from AuthContext.js
 * Handles Google OAuth callback processing from URL parameters.
 * V37.0: Extracted for maintainability.
 */
import { useEffect, useRef } from 'react';
import { getBackendUrl } from '../utils/apiUrl';
import { setSessionToken, clearSessionToken } from '../utils/secureStorage';
import devLog from '../utils/devLog';
import { fireConversion } from '../utils/googleAdsTracking';

// V37.44.44 — Google Ads sign-up conversion firing helper.
// V37.44.45 — Delegates to the centralized `googleAdsTracking` module so
// future conversion events are added once in the registry without
// touching this handler. The sessionStorage dedupe, `transport_type:
// beacon`, `event_callback`, and consent gating all live in that module.
function _fireGoogleAdsSignupConversion(source) {
  fireConversion('signup', {}, { source });
}

export function useOAuthHandler({ checkExistingSession, setIsProcessingOAuth, setLoading }) {
  const processedRef = useRef(false);
  const BACKEND_URL = getBackendUrl();

  // Primary OAuth handler
  useEffect(() => {
    if (processedRef.current) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    
    const userAgent = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(userAgent);
    const isMacOS = /Macintosh/.test(userAgent);
    const isMacOSSafari = isSafari && isMacOS && !isIOS;
    const isIOSSafari = isIOS || (isSafari && 'ontouchend' in document);
    const isAnySafari = isIOSSafari || isMacOSSafari;
    
    if (isAnySafari) {
      devLog.log('[Auth] Safari detected - using enhanced session handling');
    }
    
    try {
      localStorage.removeItem('ep_auth_in_progress');
      sessionStorage.removeItem('ep_auth_in_progress');
    } catch (e) { devLog.warn('[Auth] Storage access failed:', e.message); }
    
    if (urlParams.get('auth') === 'success') {
      processedRef.current = true;
      const sessionToken = urlParams.get('session');
      // V37.44.1: Google Ads sign-up conversion signal — backend appends
      // `&new_user=1` ONLY after a genuinely new account was inserted
      // (post-users.insert_one in /auth/google/callback). Missing / '0'
      // means an existing-user login, so no conversion is fired.
      const isNewSignup = urlParams.get('new_user') === '1';
      devLog.log('[Auth] Google OAuth success detected');

      // V37.44.44 — Fire the Google Ads sign-up conversion IMMEDIATELY
      // on new-user signal detection, BEFORE `checkExistingSession` runs
      // (which can take 100-300 ms + a network round trip to resolve)
      // and before any URL cleanup. Earlier firing + `transport_type:
      // beacon` maximises the chance the beacon leaves the browser
      // before SPA transitions. Idempotent via sessionStorage guard.
      if (isNewSignup) {
        _fireGoogleAdsSignupConversion('primary');
      }

      setIsProcessingOAuth(true);
      setLoading(true);
      
      if (sessionToken) {
        setSessionToken(sessionToken);
      }
      
      try {
        window.history.replaceState({ auth: 'processing', session: sessionToken }, '', window.location.href);
      } catch (e) {
        devLog.warn('[Auth] URL state update failed:', e);
      }
      
      const checkDelay = isAnySafari ? 300 : 100;
      setTimeout(() => {
        checkExistingSession().finally(() => {
          try {
            window.history.replaceState({ auth: 'processed' }, '', window.location.pathname);
          } catch (e) {
            devLog.warn('[Auth] Final URL cleanup failed:', e);
          }
          // V37.44.1: Google Ads "Sign-up" conversion. Fires exactly once
          // per genuine new-account creation (now fired earlier in V37.44.44;
          // this call is a belt-and-suspenders retry in case the earlier
          // immediate fire ran before gtag.js was ready to flush).
          if (isNewSignup) {
            _fireGoogleAdsSignupConversion('primary-retry');
          }
          setIsProcessingOAuth(false);
        });
      }, checkDelay);
      return;
    }
    
    if (hash && hash.includes('session_id=')) {
      setLoading(false);
    } else {
      checkExistingSession();
    }
  }, [checkExistingSession, setIsProcessingOAuth, setLoading, BACKEND_URL]);

  // Secondary handler (fallback for Safari race conditions)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    
    const historyState = window.history.state;
    if (historyState?.auth === 'processed' || historyState?.auth === 'processing') {
      return;
    }
    
    if (urlParams.get('auth') === 'success') {
      const sessionToken = urlParams.get('session');
      const isNewSignup = urlParams.get('new_user') === '1';

      // V37.44.44 — Safari fallback: also fire the conversion up-front
      // in case the primary handler was skipped due to history state.
      if (isNewSignup) {
        _fireGoogleAdsSignupConversion('secondary');
      }

      setIsProcessingOAuth(true);
      setLoading(true);
      
      if (sessionToken) {
        setSessionToken(sessionToken);
      }
      
      try {
        window.history.replaceState({ auth: 'processing', session: sessionToken }, '', window.location.href);
      } catch (e) {
        devLog.warn('[Auth] URL state update failed in secondary handler:', e);
      }
      
      setTimeout(() => {
        checkExistingSession().finally(() => {
          try {
            window.history.replaceState({ auth: 'processed' }, '', window.location.pathname);
          } catch (e) {}
          // V37.44.1: Fire Google Ads sign-up conversion in Safari fallback
          // path too (retry — V37.44.44 fired it earlier). sessionStorage
          // guard prevents double-fire.
          if (isNewSignup) {
            _fireGoogleAdsSignupConversion('secondary-retry');
          }
          setIsProcessingOAuth(false);
        });
      }, 200);
      return;
    }
    
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      if (sessionId) {
        devLog.log('[Auth] IMMEDIATE session_id processing from URL...');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
